//
// Copyright 2026 DXOS.org
//
import * as Context from 'effect/Context';
import * as Rpc from 'effect/rpc/Rpc';
import * as RpcGroup from 'effect/rpc/RpcGroup';
import * as Schema from 'effect/Schema';
import { GetSpaceSnapshotResponseSchema, SaveSpaceSnapshotResponseSchema, SignalResponseSchema, SubscribeToFeedBlocksResponseSchema, SubscribeToMetadataResponseSchema, SubscribeToSpacesResponseSchema, } from './buf/proto/gen/dxos/devtools/host_pb.js';
import { SignedMessageSchema } from './buf/proto/gen/dxos/halo/signed_pb.js';
import { SignalState } from './buf/proto/gen/dxos/mesh/signal_pb.js';
import { bufMessage, serviceError } from './service-rpc.js';
import { mutableArray, protoTimestamp, publicKey } from './service-schemas.js';
//
// RPC message schemas.
//
export const ReadyEvent = Schema.Struct({});
export const Event = Schema.Struct({
    ready: Schema.optional(ReadyEvent),
});
export const GetConfigResponse = Schema.Struct({
    /** JSON-encoded configuration object. */
    config: Schema.String,
});
export const StorageInfo = Schema.Struct({
    type: Schema.String,
    storageUsage: Schema.Number,
    originUsage: Schema.Number,
    usageQuota: Schema.Number,
});
export const StoredSnapshotInfo = Schema.Struct({
    key: Schema.String,
    size: Schema.Number,
});
export const GetSnapshotsResponse = Schema.Struct({
    snapshots: Schema.optional(mutableArray(StoredSnapshotInfo)),
});
export const ResetStorageRequest = Schema.Struct({});
export const EnableDebugLoggingRequest = Schema.Struct({
    namespaces: Schema.optional(Schema.String),
});
export const EnableDebugLoggingResponse = Schema.Struct({
    enabledNamespaces: Schema.optional(Schema.String),
});
export const SubscribeToKeyringKeysRequest = Schema.Struct({});
export const KeyRecord = Schema.Struct({
    publicKey: Schema.Uint8Array,
    privateKey: Schema.optional(Schema.Uint8Array),
});
export const SubscribeToKeyringKeysResponse = Schema.Struct({
    keys: Schema.optional(mutableArray(KeyRecord)),
});
export const SubscribeToCredentialMessagesRequest = Schema.Struct({
    spaceKey: Schema.optional(publicKey),
});
export const SubscribeToCredentialMessagesResponse = Schema.Struct({
    messages: Schema.optional(mutableArray(bufMessage(SignedMessageSchema))),
});
export const SubscribeToSpacesRequest = Schema.Struct({
    // Optional: an empty payload subscribes to all spaces (the handler treats absent spaceKeys as no filter).
    spaceKeys: Schema.optional(mutableArray(publicKey)),
});
export const SubscribeToItemsRequest = Schema.Struct({});
export const SubscribeToItemsResponse = Schema.Struct({
    /** JSON-encoded payload. */
    data: Schema.String,
});
export const SubscribeToFeedsRequest = Schema.Struct({
    // Optional: an empty payload subscribes to all feeds (the handler treats absent feedKeys as no filter).
    feedKeys: Schema.optional(mutableArray(publicKey)),
});
const feedOwnerSchema = Schema.Struct({
    identity: publicKey,
    device: publicKey,
});
const feedSchema = Schema.Struct({
    feedKey: publicKey,
    length: Schema.Number,
    bytes: Schema.Number,
    /** Bitfield of downloaded blocks. */
    downloaded: Schema.Uint8Array,
    owner: Schema.optional(feedOwnerSchema),
});
export const SubscribeToFeedsResponse = Schema.Struct({
    feeds: Schema.optional(mutableArray(feedSchema)),
});
export const SubscribeToFeedBlocksRequest = Schema.Struct({
    spaceKey: Schema.optional(publicKey),
    feedKey: Schema.optional(publicKey),
    maxBlocks: Schema.optional(Schema.Number),
});
export const GetSpaceSnapshotRequest = Schema.Struct({
    spaceKey: publicKey,
});
export const SaveSpaceSnapshotRequest = Schema.Struct({
    spaceKey: publicKey,
});
export const ClearSnapshotsRequest = Schema.Struct({});
export const GetNetworkPeersRequest = Schema.Struct({
    topic: Schema.Uint8Array,
});
export const PeerInfo = Schema.Struct({
    id: publicKey,
    /** PeerState enum. */
    state: Schema.String,
    connections: Schema.optional(mutableArray(Schema.Uint8Array)),
});
export const GetNetworkPeersResponse = Schema.Struct({
    peers: Schema.optional(mutableArray(PeerInfo)),
});
export const Topic = Schema.Struct({
    topic: publicKey,
    label: Schema.optional(Schema.String),
});
export const SubscribeToNetworkTopicsResponse = Schema.Struct({
    topics: Schema.optional(mutableArray(Topic)),
});
const signalServerSchema = Schema.Struct({
    host: Schema.String,
    state: Schema.Enum(SignalState),
    error: Schema.optional(Schema.String),
    /** Number of milliseconds before reconnection. */
    reconnectIn: Schema.Number,
    connectionStarted: protoTimestamp,
    lastStateChange: protoTimestamp,
});
export const SubscribeToSignalStatusResponse = Schema.Struct({
    servers: Schema.optional(mutableArray(signalServerSchema)),
});
export const SubscribeToSwarmInfoRequest = Schema.Struct({});
export const ConnectionEvent = Schema.Struct({
    type: Schema.String,
    newState: Schema.optional(Schema.String),
    error: Schema.optional(Schema.String),
});
export const StreamStats = Schema.Struct({
    id: Schema.Number,
    tag: Schema.String,
    contentType: Schema.optional(Schema.String),
    bytesSent: Schema.Number,
    bytesReceived: Schema.Number,
    bytesSentRate: Schema.optional(Schema.Number),
    bytesReceivedRate: Schema.optional(Schema.Number),
    writeBufferSize: Schema.optional(Schema.Number),
});
export const ConnectionInfo = Schema.Struct({
    state: Schema.String,
    sessionId: publicKey,
    remotePeerId: publicKey,
    transport: Schema.optional(Schema.String),
    protocolExtensions: Schema.optional(mutableArray(Schema.String)),
    events: Schema.optional(mutableArray(ConnectionEvent)),
    streams: Schema.optional(mutableArray(StreamStats)),
    closeReason: Schema.optional(Schema.String),
    identity: Schema.optional(Schema.String),
    readBufferSize: Schema.optional(Schema.Number),
    writeBufferSize: Schema.optional(Schema.Number),
    lastUpdate: Schema.optional(protoTimestamp),
    transportDetails: Schema.optional(Schema.String),
    transportBytesSent: Schema.optional(Schema.Number),
    transportBytesReceived: Schema.optional(Schema.Number),
    transportPacketsSent: Schema.optional(Schema.Number),
    transportPacketsReceived: Schema.optional(Schema.Number),
});
export const SwarmInfo = Schema.Struct({
    id: publicKey,
    topic: publicKey,
    label: Schema.optional(Schema.String),
    isActive: Schema.Boolean,
    connections: Schema.optional(mutableArray(ConnectionInfo)),
});
export const SubscribeToSwarmInfoResponse = Schema.Struct({
    data: Schema.optional(mutableArray(SwarmInfo)),
});
export const ExportSqliteDatabaseResponse = Schema.Struct({
    data: Schema.Uint8Array,
});
export const RunSqliteQueryRequest = Schema.Struct({
    query: Schema.String,
    /** JSON-encoded array of query parameters. */
    params: Schema.optional(Schema.String),
});
export const RunSqliteQueryResponse = Schema.Struct({
    /** JSON-encoded array of row objects. */
    rows: Schema.String,
    error: Schema.optional(Schema.String),
});
/**
 * Effect RPC definitions for `dxos.devtools.host.DevtoolsHost`.
 * Service-only payloads use Effect schemas; shared proto types remain protobuf-encoded on the wire.
 */
export class Rpcs extends RpcGroup.make(
/** Subscribe to server-to-client events. */
Rpc.make('events', {
    success: Event,
    error: serviceError,
    stream: true,
}), 
/** Get client config. */
Rpc.make('getConfig', {
    success: GetConfigResponse,
    error: serviceError,
}), Rpc.make('getStorageInfo', {
    success: StorageInfo,
    error: serviceError,
}), Rpc.make('resetStorage', {
    payload: ResetStorageRequest,
    error: serviceError,
}), Rpc.make('getSnapshots', {
    success: GetSnapshotsResponse,
    error: serviceError,
}), Rpc.make('enableDebugLogging', {
    payload: EnableDebugLoggingRequest,
    success: EnableDebugLoggingResponse,
    error: serviceError,
}), Rpc.make('disableDebugLogging', {
    payload: EnableDebugLoggingRequest,
    success: EnableDebugLoggingResponse,
    error: serviceError,
}), Rpc.make('subscribeToKeyringKeys', {
    payload: SubscribeToKeyringKeysRequest,
    success: SubscribeToKeyringKeysResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToCredentialMessages', {
    payload: SubscribeToCredentialMessagesRequest,
    success: SubscribeToCredentialMessagesResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToSpaces', {
    payload: SubscribeToSpacesRequest,
    success: bufMessage(SubscribeToSpacesResponseSchema),
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToItems', {
    payload: SubscribeToItemsRequest,
    success: SubscribeToItemsResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToFeeds', {
    payload: SubscribeToFeedsRequest,
    success: SubscribeToFeedsResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToFeedBlocks', {
    payload: SubscribeToFeedBlocksRequest,
    success: bufMessage(SubscribeToFeedBlocksResponseSchema),
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToMetadata', {
    success: bufMessage(SubscribeToMetadataResponseSchema),
    error: serviceError,
    stream: true,
}), Rpc.make('getSpaceSnapshot', {
    payload: GetSpaceSnapshotRequest,
    success: bufMessage(GetSpaceSnapshotResponseSchema),
    error: serviceError,
}), Rpc.make('saveSpaceSnapshot', {
    payload: SaveSpaceSnapshotRequest,
    success: bufMessage(SaveSpaceSnapshotResponseSchema),
    error: serviceError,
}), Rpc.make('clearSnapshots', {
    payload: ClearSnapshotsRequest,
    error: serviceError,
}), Rpc.make('getNetworkPeers', {
    payload: GetNetworkPeersRequest,
    success: GetNetworkPeersResponse,
    error: serviceError,
}), Rpc.make('subscribeToNetworkTopics', {
    success: SubscribeToNetworkTopicsResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToSignalStatus', {
    success: SubscribeToSignalStatusResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToSignal', {
    success: bufMessage(SignalResponseSchema),
    error: serviceError,
    stream: true,
}), Rpc.make('subscribeToSwarmInfo', {
    payload: SubscribeToSwarmInfoRequest,
    success: SubscribeToSwarmInfoResponse,
    error: serviceError,
    stream: true,
}), Rpc.make('exportSqliteDatabase', {
    success: ExportSqliteDatabaseResponse,
    error: serviceError,
}), Rpc.make('runSqliteQuery', {
    payload: RunSqliteQueryRequest,
    success: RunSqliteQueryResponse,
    error: serviceError,
})).prefix('DevtoolsHost.') {
}
/**
 * Effect service tag for the `DevtoolsHost` RPC handlers.
 */
export class Tag extends Context.Service()('@dxos/protocols/rpc/DevtoolsHost') {
}
//# sourceMappingURL=DevtoolsHost.js.map